/**
 * Shared photo validation utilities
 *
 * Client-side preflight checks for photo uploads (type, size).
 * Used by both profile photo and check-in photo upload flows.
 *
 * Size and type come from the metadata the image picker already returns
 * (`fileSize` / `mimeType` / `fileName`); this preflight never reads the file
 * system itself. On native the picker normalizes every pick to a JPEG of known
 * size, so the size is always present there. On web, a `blob:` URI is measured
 * directly. When the size is genuinely unknown we defer to the server's limit.
 */

import { Platform } from 'react-native';
import {
  MAX_ALT_TEXT_LENGTH,
  MAX_PHOTOS_PER_CHECK_IN,
  PHOTO_LIMIT_REACHED_CODE,
} from '@/constants/photos';
import { isRawAsset } from './image-format';

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export interface PhotoValidationInput {
  uri: string;
  mimeType?: string | null;
  /** Byte size when the caller already knows it (from the picker asset). */
  size?: number;
  fileName?: string | null;
  platform?: string;
}

export interface PhotoValidationResult {
  valid: boolean;
  error?: string;
}

export function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
}

/**
 * Validate a photo before upload (client-side preflight check).
 * Checks for unsupported RAW captures, MIME type, and file size.
 */
export async function validatePhotoBeforeUpload(
  input: PhotoValidationInput
): Promise<PhotoValidationResult> {
  const { uri, mimeType, size, fileName, platform = Platform.OS } = input;

  // Reject RAW captures (e.g. Pixel/DSLR .dng) with a clear, actionable message.
  if (isRawAsset({ mimeType, fileName })) {
    return {
      valid: false,
      error: 'RAW photos are not supported. Please choose the JPEG version of the photo.',
    };
  }

  // Validate MIME type when the picker reported one. An unknown type is not
  // coerced to a default here: native picks are normalized to JPEG upstream,
  // and the server enforces its own allowlist as a backstop.
  if (mimeType && !ALLOWED_IMAGE_TYPES.includes(mimeType.toLowerCase())) {
    return {
      valid: false,
      error: `Invalid file type: ${mimeType}. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`,
    };
  }

  // Determine the byte size. Prefer the size the caller already has; otherwise
  // measure a web blob directly. Never touch the native file system here.
  let fileSize = size;
  if (fileSize === undefined && platform === 'web' && uri.startsWith('blob:')) {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();
      fileSize = blob.size;
    } catch {
      return {
        valid: false,
        error: 'Failed to read file size. Please try again.',
      };
    }
  }

  if (fileSize !== undefined && fileSize > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `Photo is ${formatFileSize(fileSize)}; max is ${formatFileSize(MAX_FILE_SIZE)}`,
    };
  }

  return { valid: true };
}

/** Extensions accepted when a browser reports no MIME type (common for HEIC). */
const TYPELESS_IMAGE_EXTENSIONS = ['heic', 'heif'];

/**
 * Whether a browser File (picked, dropped or pasted) is an image we accept.
 * Chrome and Firefox report HEIC files with an empty type, so those are
 * matched by extension.
 */
export function isAcceptedImageFile(file: { type: string; name: string }): boolean {
  const type = file.type.toLowerCase();
  if (type) {
    return ALLOWED_IMAGE_TYPES.includes(type);
  }
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  return file.name.includes('.') && TYPELESS_IMAGE_EXTENSIONS.includes(extension);
}

/**
 * Normalize user-entered alt text the way the server does: trimmed, blank ->
 * null. Text over the server limit is cut rather than rejected; the input
 * already enforces the limit, so this only guards pasted or restored text.
 */
export function normalizeAltText(text: string | null | undefined): string | null {
  const trimmed = text?.trim() ?? '';
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_ALT_TEXT_LENGTH);
}

interface ErrorWithResponseData {
  status?: unknown;
  data?: { error?: { code?: unknown } };
}

/** Whether an upload failed because the check-in already has the maximum photos. */
export function isPhotoLimitReachedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { status, data } = error as ErrorWithResponseData;
  return status === 409 && data?.error?.code === PHOTO_LIMIT_REACHED_CODE;
}

/** A user-facing message for a failed photo upload. */
export function getPhotoUploadErrorMessage(error: unknown): string {
  if (isPhotoLimitReachedError(error)) {
    return `A check-in can have at most ${MAX_PHOTOS_PER_CHECK_IN} photos.`;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Upload failed';
}

interface FileTransferLike {
  files?: ArrayLike<File> | null;
}

/**
 * Files carried by a drop (`DataTransfer`) or paste (`clipboardData`) event.
 * Empty for text-only transfers, so callers can leave those alone.
 */
export function getTransferFiles(transfer: FileTransferLike | null | undefined): File[] {
  return transfer?.files ? Array.from(transfer.files) : [];
}

/** Optional extras for a photo upload. */
export interface PhotoUploadOptions {
  /** Description for screen readers; sent as `alt_text` after normalizing. */
  altText?: string | null;
}

/** Extra multipart fields for a photo upload, or undefined when there are none. */
export function photoUploadFields(
  options: PhotoUploadOptions = {}
): Record<string, string> | undefined {
  const altText = normalizeAltText(options.altText);
  return altText ? { alt_text: altText } : undefined;
}
